import React, { useRef, useLayoutEffect, useState } from 'react';
import { MessageSimple, useChannelStateContext, useChatContext, useMessageContext } from 'stream-chat-react';
import styles from '../styles/CustomMessage.module.css';
import { MESSAGES_WAS_DELETED, APPOINTMENT_STATUS  } from "../constants";
 import { API_URLS } from "../constants/apiUrls";
import { formatDaysAgo, formatAppointmentTimesInText, isAppointmentActionMessage } from '../utils/chatUtils';
import UserPhoto from "./shared/UserPhoto";
import { useWindowResize } from '../hooks/useWindowResize';
import { useChatStore } from "../store/useChatStore";
import CustomMessageAppointment from "./CustomMessageAppointment";

const CustomMessage = () => {
  const { message, isMyMessage } = useMessageContext();
  const { client } = useChatContext();
  const isSender = message?.user?.id === client.userID;
  const hasReplies = message?.reply_count > 0;
  const participants = message?.thread_participants ?? [];
  const latestReplyUser = participants?.length ? participants[participants.length - 1] : null;

  const isSidebarOpen = useChatStore((s) => s.isSidebarOpen);
  const { threadMessages } = useChannelStateContext();
  const messageRef = useRef(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const updateHeightRef = useRef(() => {});

  useLayoutEffect(() => {
    const inner = messageRef.current?.querySelector('.str-chat__message-inner');
    if (!inner) return;

    const updateHeight = () => {
      setInnerHeight(inner.getBoundingClientRect().height);
    };
    updateHeightRef.current = updateHeight;
    updateHeight();

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(inner);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, [message, threadMessages, isSidebarOpen]);

  useWindowResize(() => updateHeightRef.current(), []);

  // Flutter writes appointment action messages ("I have declined the appointment request for ...")
  // with a two-digit hour. Render a normalized copy so single-digit hours drop the leading zero (EV-19457)
  // without mutating the message object shared with Stream's channel state.
  const displayMessage = isAppointmentActionMessage(message)
    ? { ...message, text: formatAppointmentTimesInText(message.text) }
    : message;
    // Check if the message has a customType of "appointment"
  if (
    API_URLS?.APP_ENABLE_SCHEDULE_APPOINTMENT &&
      message?.type?.toLowerCase() === 'regular' &&
      message?.appointment?.type?.toLowerCase() === APPOINTMENT_STATUS?.APPOINTMENT?.toLowerCase() &&       
    [
      APPOINTMENT_STATUS?.REQUESTED,
      APPOINTMENT_STATUS?.ACCEPTED,
      APPOINTMENT_STATUS?.REJECTED,
      APPOINTMENT_STATUS?.CANCELED,
      APPOINTMENT_STATUS?.DELETED,
    ].includes(message.appointment.status)
  ) {
    return (
      <CustomMessageAppointment
            message={message}
            isMyMessage={isMyMessage}
            isSender={isSender}
            client={client}
      />
    );
  }

  if (message.deleted_at) {
    return (
      <div className={isMyMessage() ? "flex-col" : "flex"}>
        {!isMyMessage() && <div className="custom-avatar-fallback"></div>}
        <div>
          <div
            className={
              `str-chat__message-simple--deleted ${styles.customDeletedMessage} ` +
              (isSender ? styles.deletedMessageRight : styles.deletedMessageLeft)
            }
          >
            {MESSAGES_WAS_DELETED}
          </div>
          <div
            className={
              styles.deletedAtTime +
              ' ' +
              (isSender ? styles.deletedAtTimeRight : styles.deletedAtTimeLeft)
            }
          >
            {formatDaysAgo(message.deleted_at)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.messageWrapper} ref={messageRef}>
      <MessageSimple message={displayMessage} />
      {hasReplies && (
        <div
          className={
            `${styles.replyConnectorWrapper} ` +
            (isMyMessage() ? styles.right : styles.left)
          }
          style={{ top: `${innerHeight}px`, position: "absolute", left: isMyMessage() ? "auto" : "44px" }}
        >
          <div className={styles.curvedLine}></div>
          {latestReplyUser && (
            <div className={styles.latestReplyUserWrapper}>
              <UserPhoto
                customClass={styles.latestReplyUser}
                photo={null}
                firstName={latestReplyUser.firstName}
                lastName={latestReplyUser.lastName}
                sharedFirstName={latestReplyUser.sharedFirstName}
                sharedLastName={latestReplyUser.sharedLastName}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomMessage;